import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { storeDashboardApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import {
  Package, ShoppingBag, DollarSign, Eye, MousePointer, Plus, Pencil, Trash2,
  Loader2, AlertCircle, CheckCircle, Clock, Settings,
  Truck, MapPin, ArrowUpRight, ArrowDownRight, Bell, Info,
  TrendingUp, BarChart3, Target, LayoutDashboard
} from 'lucide-react';
import { StoreProductDialog, StoreSettingsTab } from './components';

const formatSEK = (n) => (n || 0).toLocaleString('sv-SE') + ' kr';

const TrendBadge = ({ value }) => {
  if (value === 0 || value === undefined) return <span className="text-xs text-gray-400">{'\u2014'}</span>;
  const isUp = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${isUp ? 'text-emerald-600' : 'text-red-500'}`} data-testid="trend-badge">
      {isUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {isUp ? '+' : ''}{value}%
    </span>
  );
};

const InfoTooltip = ({ text }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="inline-flex ml-1 text-gray-400 hover:text-gray-600 transition-colors" data-testid="info-tooltip">
        <Info className="h-3.5 w-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[260px] text-sm"><p>{text}</p></TooltipContent>
  </Tooltip>
);

const StatCard = ({ icon: Icon, label, value, trend, color, tooltip }) => {
  const colors = { blue: 'bg-blue-500', green: 'bg-emerald-500', purple: 'bg-violet-500', orange: 'bg-amber-500', teal: 'bg-teal-500', rose: 'bg-rose-500' };
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 transition-all hover:shadow-md" data-testid={`stat-card-${label.toLowerCase().replace(/[\s()]/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${colors[color] || colors.blue} flex items-center justify-center`}><Icon className="h-5 w-5 text-white" /></div>
        {trend !== undefined && <TrendBadge value={trend} />}
      </div>
      <p className="text-2xl font-black text-[#0F172A] tracking-tight">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5 flex items-center">{label}{tooltip && <InfoTooltip text={tooltip} />}</p>
    </div>
  );
};

const OrderStatusBadge = ({ status }) => {
  const map = {
    new: { label: 'Ny', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    pending: { label: 'Väntande', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    paid: { label: 'Betald', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    confirmed: { label: 'Bekräftad', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    shipped: { label: 'Skickad', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    delivered: { label: 'Levererad', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    completed: { label: 'Avslutad', cls: 'bg-gray-50 text-gray-700 border-gray-200' },
    cancelled: { label: 'Avbruten', cls: 'bg-red-50 text-red-600 border-red-200' },
  };
  const c = map[status] || { label: status || 'Okänd', cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${c.cls}`} data-testid={`order-status-${status}`}>{c.label}</span>;
};

export const StoreDashboardPage = () => {
  const navigate = useNavigate();
  const { token, isStoreOwner, isAdmin, loading: authLoading, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { toast.error('Du måste vara inloggad'); navigate('/logga-in'); return; }
    if (!authLoading && !isStoreOwner && !isAdmin) { toast.error('Du har inte behörighet att se denna sida'); navigate('/'); }
  }, [isStoreOwner, isAdmin, authLoading, isAuthenticated, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [storeRes, productsRes, ordersRes, statsRes, notifRes] = await Promise.all([
        storeDashboardApi.getMyStore(),
        storeDashboardApi.getProducts(),
        storeDashboardApi.getOrders(),
        storeDashboardApi.getStatistics(),
        storeDashboardApi.getNotifications().catch(() => ({ data: { notifications: [] } })),
      ]);
      setStore(storeRes.data);
      setProducts(productsRes.data.products);
      setOrders(ordersRes.data.orders);
      setStatistics(statsRes.data);
      setNotifications(notifRes.data?.notifications || []);
    } catch (error) {
      console.error('Error fetching store data:', error);
      if (error.response?.status === 404) toast.error('Du har ingen butik registrerad');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if ((isStoreOwner || isAdmin) && token) fetchData();
  }, [isStoreOwner, isAdmin, token, fetchData]);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Är du säker på att du vill ta bort produkten?')) return;
    try { await storeDashboardApi.deleteProduct(productId); toast.success('Produkten har tagits bort'); fetchData(); }
    catch { toast.error('Kunde inte ta bort produkten'); }
  };

  if (authLoading || loading) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73]" /></div>;

  if (!store) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Ingen butik hittades</h2>
        <p className="text-gray-500 mb-4">Du har ingen registrerad butik.</p>
        <Link to="/bli-partner/anslut-byggvaruhus"><Button className="bg-[#FF8A00] hover:bg-[#e67c00]">Kontakta oss om partnerskap</Button></Link>
      </div>
    </div>
  );

  const isPending = store.status === 'pending';
  const isApproved = store.status === 'approved';
  const newOrderCount = orders.filter(o => o.status === 'new' || o.status === 'pending' || o.status === 'paid').length;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="bg-[#F8FAFC] min-h-screen" data-testid="store-dashboard-page">
        <div className="container-main py-8">

          {/* ── Sidhuvud ── */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-[#0F172A]">{store.name}</h1>
              <div className="flex items-center gap-3 mt-2">
                {isPending && <span className="flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium"><Clock className="h-4 w-4" /> Väntar på godkännande</span>}
                {isApproved && <span className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium"><CheckCircle className="h-4 w-4" /> Godkänd</span>}
                <span className="text-sm text-gray-400">Provision: {(store.commission_rate * 100).toFixed(0)} %</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Notiser */}
              <div className="relative">
                <button onClick={() => setShowNotifications(!showNotifications)} className="relative w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors" data-testid="notification-bell">
                  <Bell className="h-5 w-5 text-gray-600" />
                  {notifications.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-count">{notifications.length}</span>}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden" data-testid="notification-dropdown">
                    <div className="p-4 border-b border-gray-100"><h3 className="font-bold text-[#0F172A]">Notiser</h3></div>
                    {notifications.length > 0 ? (
                      <div className="max-h-60 overflow-y-auto">
                        {notifications.map((n, i) => (
                          <div key={i} className="px-4 py-3 border-b border-gray-50 flex items-start gap-3">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.priority === 'high' ? 'bg-red-500' : n.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                            <p className="text-sm text-gray-700">{n.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : <p className="p-4 text-sm text-gray-400">Inga notiser just nu</p>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {isPending && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-yellow-800">Din butik väntar på godkännande</h3>
                  <p className="text-yellow-700 mt-1">Vi granskar din ansökan och återkommer inom 1–2 arbetsdagar.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── NAVIGATION – Flikar högst upp ── */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white border border-gray-200 rounded-xl p-1 shadow-sm mb-8" data-testid="store-tabs">
              <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-[#003B73] data-[state=active]:text-white" data-testid="tab-overview">
                <LayoutDashboard className="h-4 w-4 mr-2" /> Översikt
              </TabsTrigger>
              <TabsTrigger value="products" className="rounded-lg data-[state=active]:bg-[#003B73] data-[state=active]:text-white" data-testid="tab-products">
                <Package className="h-4 w-4 mr-2" /> Produkter <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded text-xs font-bold">{products.length}</span>
              </TabsTrigger>
              <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-[#003B73] data-[state=active]:text-white" data-testid="tab-orders">
                <ShoppingBag className="h-4 w-4 mr-2" /> Ordrar
                {newOrderCount > 0 && <span className="ml-1.5 bg-red-500 text-white px-1.5 py-0.5 rounded text-xs font-bold" data-testid="new-orders-badge">{newOrderCount}</span>}
              </TabsTrigger>
              <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-[#003B73] data-[state=active]:text-white" data-testid="tab-settings">
                <Settings className="h-4 w-4 mr-2" /> Inställningar
              </TabsTrigger>
            </TabsList>

            {/* ═══════════ ÖVERSIKT ═══════════ */}
            <TabsContent value="overview">
              <div className="space-y-8">

                {/* Statistikkort */}
                <div>
                  <h2 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                    Översikt
                    <InfoTooltip text="Summering av trafik och aktivitet de senaste 30 dagarna." />
                  </h2>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                    <StatCard icon={Eye} label="Visningar" value={statistics.total_views || 0} trend={statistics.trends?.views} color="blue" tooltip="Antal gånger dina produkter har visats i listor, kategorier eller sökningar." />
                    <StatCard icon={MousePointer} label="Klick" value={statistics.total_clicks || 0} trend={statistics.trends?.clicks} color="green" tooltip="Antal gånger någon har klickat in på en av dina produkter." />
                    <StatCard icon={Target} label="CTR (klickfrekvens)" value={`${statistics.ctr || 0} %`} color="teal" tooltip="Klickfrekvens = Klick / Visningar. Visar hur stor andel av visningarna som leder till ett klick." />
                    <StatCard icon={TrendingUp} label="Konverteringsgrad" value={`${statistics.conversion_rate || 0} %`} color="purple" tooltip="Andel klick som leder till ett köp. Konverteringsgrad = Försäljningar / Klick." />
                    <StatCard icon={ShoppingBag} label="Försäljningar" value={statistics.total_sales || 0} trend={statistics.trends?.sales} color="orange" tooltip="Totalt antal sålda produkter under perioden." />
                    <StatCard icon={DollarSign} label="Nettointäkter" value={formatSEK(statistics.net_earnings)} trend={statistics.trends?.revenue} color="rose" tooltip="Dina intäkter efter att provision har dragits av." />
                  </div>
                </div>

                {/* Senaste 30 dagarna */}
                <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><BarChart3 className="h-5 w-5 text-blue-600" /></div>
                  <div>
                    <p className="text-xl font-black text-[#0F172A]">{formatSEK(statistics.revenue_last_30)}</p>
                    <p className="text-sm text-gray-500 flex items-center">Senaste 30 dagarna<InfoTooltip text="Total omsättning för dina produkter under de senaste 30 dagarna." /></p>
                  </div>
                </div>

                {/* Dynamisk banner */}
                <div className="bg-gradient-to-r from-[#FF8A00] to-[#FF6B00] rounded-2xl p-6 shadow-lg">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-white text-center md:text-left">
                      <h2 className="text-xl font-black mb-1">
                        {products.length > 0 ? `Du har ${products.length} produkt${products.length > 1 ? 'er' : ''} live!` : 'Lägg upp din första produkt!'}
                      </h2>
                      <p className="text-white/80 text-sm">
                        {statistics.total_views > 0
                          ? `Dina produkter har fått ${(statistics.total_views || 0).toLocaleString('sv-SE')} visningar den här perioden. Lägg upp fler produkter för att öka din försäljning.`
                          : 'Nå tusentals kunder genom att lägga upp dina produkter på ByggFynd.'}
                      </p>
                    </div>
                    <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="lg" className="bg-white text-[#FF8A00] hover:bg-gray-100 font-bold rounded-xl shadow-lg flex-shrink-0" onClick={() => setEditProduct(null)} data-testid="create-ad-main-btn">
                          <Plus className="h-5 w-5 mr-2" />Lägg upp produkt
                        </Button>
                      </DialogTrigger>
                      <StoreProductDialog product={editProduct} token={token} onSuccess={() => { setIsProductDialogOpen(false); setEditProduct(null); fetchData(); }} />
                    </Dialog>
                  </div>
                </div>

                {/* Produktprestanda */}
                <div>
                  <h2 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-[#003B73]" /> Produktprestanda
                  </h2>
                  <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="product-performance">
                    <p className="text-xs text-gray-400 mb-4">Mest visade produkter</p>
                    {(statistics.top_viewed || []).length > 0 ? (
                      <div className="space-y-1">
                        <div className="grid grid-cols-[24px_36px_1fr_80px_80px_80px] items-center gap-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 pb-2 border-b border-gray-100">
                          <span>#</span><span></span><span>Produkt</span><span className="text-right">Visningar</span><span className="text-right">Klick</span><span className="text-right">Konvertering</span>
                        </div>
                        {statistics.top_viewed.map((p, i) => {
                          const clicks = p.clicks || Math.round((p.views || 0) * (statistics.ctr || 0) / 100);
                          const conv = clicks > 0 ? ((p.sales || 0) / clicks * 100).toFixed(1) : '0.0';
                          return (
                            <div key={p.id} className="grid grid-cols-[24px_36px_1fr_80px_80px_80px] items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                              <span className="text-xs font-bold text-gray-400 text-right">{i + 1}</span>
                              <div className="w-9 h-9 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                              </div>
                              <p className="text-sm font-medium text-gray-800 truncate">{p.title}</p>
                              <p className="text-sm font-semibold text-gray-600 text-right">{(p.views || 0).toLocaleString('sv-SE')}</p>
                              <p className="text-sm text-gray-500 text-right">{clicks.toLocaleString('sv-SE')}</p>
                              <p className="text-sm text-gray-500 text-right">{conv} %</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : <p className="text-sm text-gray-400">Ingen data ännu</p>}
                  </div>
                </div>

              </div>
            </TabsContent>

            {/* ═══════════ PRODUKTER ═══════════ */}
            <TabsContent value="products">
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex flex-wrap justify-between items-center gap-3">
                  <h2 className="text-lg font-bold text-[#0F172A]">Mina produkter</h2>
                  <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#FF8A00] hover:bg-[#e67c00] rounded-xl" onClick={() => setEditProduct(null)} disabled={!isApproved} data-testid="add-product-btn">
                        <Plus className="h-4 w-4 mr-2" />Lägg till produkt
                      </Button>
                    </DialogTrigger>
                    <StoreProductDialog product={editProduct} token={token} onSuccess={() => { setIsProductDialogOpen(false); fetchData(); }} />
                  </Dialog>
                </div>
                {products.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50/80 border-b border-gray-100">
                          <th className="text-left p-4 font-semibold text-gray-600">Produkt</th>
                          <th className="text-left p-4 font-semibold text-gray-600 hidden md:table-cell">Kategori</th>
                          <th className="text-right p-4 font-semibold text-gray-600">Pris</th>
                          <th className="text-right p-4 font-semibold text-gray-600 hidden md:table-cell">Lager</th>
                          <th className="text-right p-4 font-semibold text-gray-600 hidden lg:table-cell">Visningar</th>
                          <th className="text-right p-4 font-semibold text-gray-600">Åtgärder</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => (
                          <tr key={product.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors" data-testid={`product-row-${product.id}`}>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 border border-gray-200">
                                  {product.images?.[0] ? <img src={product.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                                </div>
                                <div className="min-w-0">
                                  <span className="font-semibold text-gray-900 truncate block">{product.title}</span>
                                  {product.original_price && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">Kampanj</span>}
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-gray-500 hidden md:table-cell">{product.category_name}</td>
                            <td className="p-4 text-right">
                              <span className="font-semibold text-gray-900">{(product.price || 0).toLocaleString('sv-SE')} kr</span>
                              {product.original_price && <span className="text-xs text-gray-400 line-through ml-1">{(product.original_price || 0).toLocaleString('sv-SE')} kr</span>}
                            </td>
                            <td className="p-4 text-right hidden md:table-cell">
                              <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${product.stock > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                {product.stock > 0 ? `${product.stock} st` : 'Slutsåld'}
                              </span>
                            </td>
                            <td className="p-4 text-right text-gray-500 hidden lg:table-cell">{(product.views || 0).toLocaleString('sv-SE')}</td>
                            <td className="p-4 text-right">
                              <div className="flex justify-end gap-1.5">
                                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => { setEditProduct(product); setIsProductDialogOpen(true); }} title="Redigera"><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteProduct(product.id)} title="Ta bort"><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Du har inga produkter ännu.</p>
                    {isApproved && <p className="text-sm text-gray-400 mt-2">Klicka på &quot;Lägg till produkt&quot; för att börja.</p>}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ═══════════ ORDRAR ═══════════ */}
            <TabsContent value="orders">
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100"><h2 className="text-lg font-bold text-[#0F172A]">Orderhistorik</h2></div>
                {orders.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50/80 border-b border-gray-100">
                          <th className="text-left p-4 font-semibold text-gray-600">Ordernummer</th>
                          <th className="text-left p-4 font-semibold text-gray-600 hidden md:table-cell">Kund</th>
                          <th className="text-left p-4 font-semibold text-gray-600 hidden lg:table-cell">Datum</th>
                          <th className="text-left p-4 font-semibold text-gray-600">Status</th>
                          <th className="text-right p-4 font-semibold text-gray-600">Intäkt</th>
                          <th className="text-right p-4 font-semibold text-gray-600 hidden md:table-cell">Provision</th>
                          <th className="text-right p-4 font-semibold text-gray-600">Netto</th>
                          <th className="text-left p-4 font-semibold text-gray-600 hidden lg:table-cell">Leverans</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors" data-testid={`order-row-${order.id}`}>
                            <td className="p-4">
                              <span className="font-mono text-xs font-semibold text-[#003B73] bg-blue-50 px-2 py-1 rounded">{(order.order_number || order.id?.slice(0, 8)).toUpperCase()}</span>
                            </td>
                            <td className="p-4 hidden md:table-cell">
                              <p className="text-sm font-medium text-gray-700">{order.buyer?.name || '\u2014'}</p>
                              <p className="text-xs text-gray-400">{order.buyer?.email}</p>
                            </td>
                            <td className="p-4 text-gray-500 text-xs hidden lg:table-cell">{new Date(order.created_at).toLocaleDateString('sv-SE')}</td>
                            <td className="p-4"><OrderStatusBadge status={order.status} /></td>
                            <td className="p-4 text-right font-semibold text-gray-900">{formatSEK(order.store_revenue)}</td>
                            <td className="p-4 text-right hidden md:table-cell"><span className="text-xs font-semibold text-red-500">{'\u2212'}{formatSEK(order.store_commission)}</span></td>
                            <td className="p-4 text-right font-bold text-emerald-600">{formatSEK(order.store_net)}</td>
                            <td className="p-4 hidden lg:table-cell">
                              {order.delivery_method === 'pickup'
                                ? <span className="flex items-center gap-1 text-xs text-blue-600"><MapPin className="h-3.5 w-3.5" /> Hämtas i butik</span>
                                : <span className="flex items-center gap-1 text-xs text-gray-500"><Truck className="h-3.5 w-3.5" /> {order.carrier || 'PostNord'}</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center"><ShoppingBag className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Inga ordrar ännu.</p></div>
                )}
              </div>
            </TabsContent>

            {/* ═══════════ INSTÄLLNINGAR ═══════════ */}
            <TabsContent value="settings">
              <StoreSettingsTab store={store} token={token} onUpdate={fetchData} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default StoreDashboardPage;
